import { doc, getDoc } from 'firebase/firestore';
import { db } from '../Components/auth/firebaseConfig';
import { callBackendFunction } from './renderCallable';

export const CENTRAL_API_ERROR_MESSAGES = {
  INSUFFICIENT_CREDITS: 'Not enough credits for this action.',
  TENANT_INACTIVE: 'This tenant is inactive. Please contact your administrator.',
  BILLING_INACTIVE: 'Billing is inactive for this tenant.',
  APP_ACCESS_DENIED: 'Your current plan does not include access to this application.',
  ACTION_NOT_ALLOWED: 'This action is not available in your current plan.',
  INVALID_ACTION: 'This action is not configured for credit usage.',
  TRANSACTION_FAILED: 'Could not process credits. Please try again.',
  INVALID_AUTH: 'Could not verify the licensing request.',
  PERMISSION_DENIED: 'You do not have permission to perform this action.',
  INVALID_TENANT: 'Tenant not found.',
  INVALID_RULE: 'This action is not available in the selected plan.',
  API_UNAVAILABLE: 'Could not connect to the licensing server. Please try again.',
};

const cleanFailure = (reason = 'API_UNAVAILABLE', extra = {}) => ({
  success: false,
  reason,
  message: CENTRAL_API_ERROR_MESSAGES[reason] || extra.message || reason,
  ...extra,
});

const normalizeProxyResult = (response) => {
  const data = response?.data || response;
  if (!data) return cleanFailure();
  if (data.success === false) {
    return cleanFailure(data.reason, data);
  }
  return data;
};

const callProxy = async (operation, payload = {}) => {
  try {
    return normalizeProxyResult(await callBackendFunction({
      baseUrl: process.env.REACT_APP_OVPLAN_BACKEND_URL,
      name: 'ovplanCentralTenantProxy',
      payload: { operation, ...payload },
    }));
  } catch (error) {
    return cleanFailure('API_UNAVAILABLE', { message: error.message });
  }
};

const buildLicenseSnapshot = (tenantId, source = {}) => ({
  tenantId,
  planId: source.planId || source.plan || '',
  status: source.status || 'active',
  billingStatus: source.billingStatus || 'active',
  remainingCredits: Number(source.remainingCredits || 0),
  usedCredits: Number(source.usedCredits || 0),
  userLimit: Number(source.userLimit || 0),
  activeUsers: Number(source.activeUsers || 0),
  allowedApps: Array.isArray(source.allowedApps) ? source.allowedApps : [],
  sourceVersion: Number(source.sourceVersion || 0),
  lastSyncedAt: source.lastSyncedAt || null,
});

export const updateLocalTenantLicenseSnapshot = async (tenantId, source = {}) => {
  if (!tenantId) return null;
  // License snapshots are written by trusted backend code only. This helper is
  // kept for backward-compatible imports, but intentionally does not write to
  // Firestore from the browser.
  return buildLicenseSnapshot(tenantId, source);
};

export const getLocalTenantLicenseSnapshot = async (tenantId) => {
  if (!tenantId) return null;
  const snap = await getDoc(doc(db, 'tenantLicenses', tenantId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const getTenantLicense = async (tenantId) => {
  return callProxy('getTenantLicense', { tenantId });
};

export const syncTenantLicense = async (tenantId) => {
  return callProxy('syncTenantLicense', { tenantId });
};

export const getPricingPlans = async () => callProxy('getPricingPlans');

export const consumeCredits = async ({
  tenantId,
  action,
  userId,
  referenceId,
}) => {
  return callProxy('consumeCredits', {
    tenantId,
    action,
    userId,
    referenceId,
  });
};

export const assignPlanToTenant = async ({ tenantId, planId }) =>
  callProxy('assignPlanToTenant', { tenantId, planId });

export const addCreditsToTenant = async ({ tenantId, creditsToAdd }) =>
  callProxy('addCreditsToTenant', { tenantId, creditsToAdd });

export const setTenantStatus = async ({ tenantId, status }) =>
  callProxy('setTenantStatus', { tenantId, status });
